import React, {useEffect, useState} from 'react';
import './login.scss';
import TextInput from '../Form/TextInput/TextInput';
import PasswordInput from '../Form/PasswordInput/PasswordInput';
import Button, {LoadingButton} from '../Form/Button/Button';
import {emptyString, maskEmail} from '../../utils/helpers';
import withFeatureFlags from '../FeatureFlags/withFeatureFlags';
import useFeatureVariable from '../FeatureFlags/useFeatureVariable';
import {useLogger} from '../../contexts/Logger';
import {useCurrentUser} from '../../contexts/CurrentUserProvider';
import {getFailedPasswordPolicyRules, isPasswordPolicyCompliant} from '../../utils/passwordPolicy';
import {useUnifiedAnalytics} from '../../contexts/AnalyticsWrapper';

/** @import { LoginProps } from './LoginTypes' */

import policyWarningIcon from '../../assets/images/login-policy-warning.svg';
import bottomLeftCornerImg from "../../assets/images/Stripes Light Bottom Left.svg";
import TopRightCornerImg from "../../assets/images/Stripes Light Top Right.svg";
import CommonContainerStrips from "./CommonContainerStrips";

function Login(/** @type {LoginProps} */ {featureEnabled}) {

    const currentUser = useCurrentUser();
    const logger = useLogger();
    const {trackPopupViewedEvent, trackPopupClickedEvent} = useUnifiedAnalytics();

    const showNotification = featureEnabled('EnableLoginNotification');
    const notificationTitle = useFeatureVariable('EnableLoginNotification', 'Title');
    const notificationBody = useFeatureVariable('EnableLoginNotification', 'Body');
    const passwordPolicyEnabled = featureEnabled('DashboardPasswordPolicyV2Enabled');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [passwordPolicyFailed, setPasswordPolicyFailed] = useState(false);

    useEffect(() => {
        logger.info('Login page loaded');
        if (currentUser && currentUser.logout) {
            logger.info(`Logging out current session for customer ${currentUser.customerId || 'unknown'}, was authenticated: ${currentUser.isAuthenticated ? 'YES' : 'NO'}`);
            currentUser.logout();
        }

        let storedUsername = localStorage.getItem('username');
        if (storedUsername) {
            setEmail(storedUsername);
        }
    }, []);

    useEffect(() => {
        if (passwordPolicyFailed) {
            trackPopupViewedEvent(
                'password-policy',
                'We\'ve improved our password security',
                'Investec-Save-secure|roadblock_load|password policy warning',
                'Password Reset_We\'ve improved our password security',
                'Password Reset'
            );
        }
    }, [passwordPolicyFailed]);

    const onLogin = async (/** @type {React.MouseEvent<HTMLLabelElement, MouseEvent>} */ e) => {
        e.preventDefault();
        const maskedEmail = maskEmail(email);
        logger.info(`Login submitted for ${maskedEmail}`);

        if (passwordPolicyEnabled && !isPasswordPolicyCompliant(password, email)) {
            const failedRules = getFailedPasswordPolicyRules(password, email);
            const failedRuleSummary = failedRules
                .map(rule => `${rule.key}: ${rule.detail}`)
                .join('; ');

            setPasswordPolicyFailed(true);
            logger.warn(`Login blocked for ${maskedEmail}: password does not meet policy standards. Failed rules: ${failedRuleSummary}`);
            return;
        }

        try {
            setIsLoading(true);
            persistUsername();
            logger.info(`Calling login for ${maskedEmail}`);
            if (currentUser && currentUser.login) {
                await currentUser.login(email, password);
                logger.info(`Login authentication accepted for ${maskedEmail}`);
            }
        } catch (err) {
            setIsLoading(false);
            setErrorText(err instanceof Error ? err.message : 'An error occurred');
            logger.error(`Login failed for ${maskedEmail}`, {}, err);
        }
    };

    const persistUsername = () => {
        if (rememberMe) {
            localStorage.setItem("username", email);
        } else {
            localStorage.removeItem("username");
        }
    }

    const toggleRememberMe = () => {
        setRememberMe(value => !value);
    };

    const setAndValidate = (/** @type {string} */ val, /** @type {React.Dispatch<React.SetStateAction<string>>} */ setter) => {
        setter(val);
        setErrorText('');
        setPasswordPolicyFailed(false);
    };

    const trackResetPasswordJourneyClick = () => {
        trackPopupClickedEvent(
            'password-policy',
            'We\'ve improved our password security',
            'Reset password',
            'Investec-Save-secure|roadblock_click|reset password',
            'Password Reset_We\'ve improved our password security',
            'Password Reset'
        );
    };

    if (currentUser && currentUser.isAuthenticated) {
        logger.info(`Authenticated customer ${currentUser.customerId || 'unknown'}, redirecting to home`);
        window.location.href = '/';
        return null;
    }

    const renderLoadingButton = (/** @type {string} */ label, /** @type {boolean} */ isLoading, /** @type {boolean} */ disabled, /** @type {() => void} */ callback) => {
        return isLoading ?
            <LoadingButton customClassNames={['loginButton', 'button--primary']}/> :
            <Button
                customClassNames={['loginButton', 'button--primary']}
                disabled={disabled} label={label} onClick={callback}/>
    };

    return (
        <CommonContainerStrips propData={{
            bottomLeftImg: bottomLeftCornerImg, topRightImg: TopRightCornerImg, children: (
                <div className="loginP3__ContainerView">
                    {showNotification && (<>
                        <div className="login__notification">
                            <div className="login__notification__message">
                                {notificationTitle &&
                                    <div className="login__notification__title">{notificationTitle}</div>}
                                {notificationBody && <div className="login__notification__body"
                                                          dangerouslySetInnerHTML={{__html: notificationBody}}/>}
                            </div>
                        </div>
                        <div className="login__space"/>
                    </>)}
                    <h1 className="login__title login__title--margin-bottom login__mobiletitle">Log in</h1>
                    <div className="login__space">
                    </div>
                    <label className="EmailLabel">Your email address</label>
                    <TextInput className="login__input" type="email" value={email}
                               onChange={v => setAndValidate(v, setEmail)}/>
                    <label className="passwordLabel">Your password</label>
                    <PasswordInput value={password}
                                   onChange={v => setAndValidate(v, setPassword)}
                    />
                    {!passwordPolicyFailed && (
                        <div className="login__error">
                            <span className="login__error--wrap">{errorText}</span>
                        </div>
                    )}
                    {passwordPolicyFailed && (
                        <div className="login__policy-warning" role="alert">
                            <img
                                alt=""
                                aria-hidden="true"
                                className="login__policy-warning__icon"
                                src={policyWarningIcon}
                            />
                            <div className="login__policy-warning__content">
                                <h2 className="login__policy-warning__title">
                                    We've improved our password security
                                </h2>
                                <p className="login__policy-warning__message">
                                    Please reset your password &ndash; we'll give you guidance on how to make it stronger.
                                </p>
                                <div className="login__policy-warning__actions">
                                    <a href="/reset-password" className="login__policy-warning__button" onClick={trackResetPasswordJourneyClick}>
                                        Reset password
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="loginP3__optionsGroup">
                        <div className="loginP3__rememberMeWrapper">
                            <div className="loginP3__checkboxWrapper">
                                <input type="checkbox" id="rememberMe" className="loginP3__checkboxRemember"
                                       onChange={toggleRememberMe} checked={rememberMe}
                                       disabled={passwordPolicyFailed}/>
                            </div>
                            <label
                                htmlFor="rememberMe"
                                className={`loginP3__checkboxLabel ${passwordPolicyFailed ? 'loginP3__checkboxLabel--disabled' : ''}`}
                            >
                                Remember me
                            </label>
                        </div>
                        <a
                            href="/login-help"
                            aria-disabled={passwordPolicyFailed}
                            className={`loginP3__troubleLink ${passwordPolicyFailed ? 'loginP3__troubleLink--disabled' : ''}`}
                            onClick={passwordPolicyFailed ? e => e.preventDefault() : undefined}
                            tabIndex={passwordPolicyFailed ? -1 : undefined}
                        >
                            Having trouble logging in?
                        </a>
                    </div>
                    {renderLoadingButton("Log in", isLoading, passwordPolicyFailed || !(!emptyString(email) && !emptyString(password)), onLogin)}
                </div>
            )
        }}/>
    );
}

export default withFeatureFlags(Login);
